/**
 * HubMyTasks — the home-page "My tasks" agenda (Workspace v3).
 *
 * One forward-looking, date-grouped list (Overdue / Today / This week / Upcoming)
 * that unifies a person's whole plate: the cases on their desk (owner OR
 * collaborator) AND their own one-off + recurring tasks. The single
 * owner+collaborator "my plate" surface — the standalone Workspace "My Work" +
 * "All tasks" views are retired in favour of it.
 *
 * Read = listWorkItems({ for_user: me }) (RLS-scoped; derives `my_role` per row).
 * Mine vs shared is shown by COLOUR + glyph: a filled sage dot + sage
 * left-border = yours; a gold ring + gold left-border + the OWNER's champagne
 * avatar = a case shared with you; a terracotta chip = overdue. LP-16: a card
 * you are only a collaborator on also says "Shared with you" in words and
 * carries Leave, which removes your own collaborator row (the server admits
 * that under manage_own_tasks too) and nobody else's.
 *
 * Recurring tasks carry a Repeat glyph + a cadence subline (recurrence_summary).
 * Ticking a recurring task ADVANCES it (logs the completion and rolls the same
 * card forward); a one-off task's circle marks it done. "Add a task" expands an
 * inline form whose Repeats picker creates a recurring series.
 *
 * Design target: docs design mock (Amanda 2026-06-27), reconciled 2026-06-28 —
 * serif case names + Inter task titles, pill date chips, graded bucket headers,
 * a sand panel, the owner's trailing champagne avatar; the compact eyebrow header
 * and the collapse-on-click add row are deliberate (the Hub hero already carries
 * the brand title).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CLIENT_BUBBLE_CLASS } from '@/components/workspace/ClientBubble';
import { Repeat, Plus, Check, ChevronRight } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { canManageOwnTasks, canManageWorkItems, canViewAnyClientRoster } from '@/lib/capabilities';
import { ClientLookupField, type ClientLookupPick } from '@/components/aiTools/ClientLookupField';
import { Chip } from '@/components/primitives/Chip';
import { FlooredTitle } from '@/components/workspace/FlooredTitle';
import { displayValue } from '@/lib/valueCase';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { readTaskCategories, taskCategoryChipClass, taskCategoryLabel } from '@/lib/taskCategories';
import {
  useWorkItems,
  useCompletedTasks,
  usePatchWorkItem,
  useCreateWorkItem,
  useCompleteRecurringTask,
  useCreateRecurringSeries,
  useRemoveCollaborator,
} from '@/lib/workspace';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { bucketOf, ORDER, type Bucket } from '@/components/workspace/workBuckets';
import { shortDate, initials } from '@/components/workspace/workspaceFormat';
import {
  RecurrencePicker,
  EMPTY_RECURRENCE,
  recurrenceAnchorReady,
  recurrenceAnchors,
  type RecurrenceDraft,
} from '@/components/workspace/RecurrencePicker';
import { SkeletonRow } from '@/components/Skeleton';
import { ErrorBanner } from '@/components/ErrorBanner';
import type { WorkItemRow, WorkItemSort, CreateRecurringSeriesInput, CompletedTaskRow } from '@/lib/api';
import { SelectField } from '@/components/primitives/SelectField';
import { Button } from '@/components/primitives/Button';
import { DateInput } from '@/components/primitives/DateInput';
import { GroupHead, ROW_CAP, ROW_LIST, SECTION_CARD, ShowAllToggle, type GroupTone } from '@/components/home/RailParts';

function rowTitle(t: WorkItemRow): string {
  return t.display_name || t.title || t.prospect_name || 'Untitled';
}

/** The card's secondary line: a recurring task shows its cadence; a case shows
 *  stage · service · unit (next-action, type, location); a one-off task shows
 *  its description. */
function rowSubline(t: WorkItemRow): string | null {
  if (t.recurrence_id && t.recurrence_summary) return t.recurrence_summary;
  if (t.kind === 'intake') {
    const parts = [
      t.current_stage_label,
      t.service_requested ? displayValue(t.service_requested) : null,
      t.unit_name,
    ].filter((x): x is string => !!x);
    return parts.length > 0 ? parts.join(' · ') : t.referral_text;
  }
  return t.description;
}

/** Per-bucket header tint (terracotta → sage → warm) + the date-chip pill. */
const BUCKET_HEAD: Record<Bucket, string> = {
  Overdue: 'text-pepper',
  Today: 'text-sage-500',
  'This week': 'text-sage-600',
  Upcoming: 'text-warm-600',
};
/** LP-24: a rail bucket head's register (RailParts). Overdue asks
 *  something of the reader; every other bucket is a plain fact. */
const BUCKET_TONE: Record<Bucket, GroupTone> = {
  Overdue: 'attention',
  Today: 'neutral',
  'This week': 'neutral',
  Upcoming: 'neutral',
};

const BUCKET_CHIP: Record<Bucket, string> = {
  Overdue: 'bg-pepper-soft text-pepper',
  Today: 'bg-gold-soft text-warm-700',
  'This week': 'bg-sage-50 text-sage-500',
  Upcoming: 'bg-sage-50 text-sage-500',
};

/** WS-7 sort options for the agenda ("date order + overdue (or an ability
 *  to sort)"). Due date is the default: the Overdue/Today/… buckets ARE
 *  the attention register, now due-ordered within. Category regroups by
 *  the vocabulary label; Recently updated is a flat recency list. */
const SORT_OPTIONS: { value: WorkItemSort; label: string }[] = [
  { value: 'due_date', label: 'Due date' },
  { value: 'category', label: 'Category' },
  { value: 'recently_updated', label: 'Recently updated' },
];

/** A rendered group of agenda rows: due-date buckets, category groups, or
 *  the single flat recency section (head '' = no header row). Rows keep
 *  the server's order within each section (due-soonest under both date
 *  and category sorts; recency under Recently updated). */
interface AgendaSection {
  key: string;
  head: string;
  headClass: string;
  items: WorkItemRow[];
}

const GROUP_HEAD = 'text-sage-500';
const CASES_GROUP = 'Cases';
const UNCATEGORIZED_GROUP = 'No category';

function sectionsFor(
  sort: WorkItemSort,
  open: WorkItemRow[],
  categoryLabels: string[],
  labelOf: (t: WorkItemRow) => string | null,
): AgendaSection[] {
  if (sort === 'recently_updated') {
    return [{ key: 'recent', head: '', headClass: '', items: open }];
  }
  if (sort === 'category') {
    // Cases first (a case isn't categorizable — it keeps its visibility),
    // then the vocabulary in its configured order, then uncategorized tasks.
    const byHead = new Map<string, WorkItemRow[]>();
    for (const it of open) {
      const head =
        it.kind !== 'task' ? CASES_GROUP : (labelOf(it) ?? UNCATEGORIZED_GROUP);
      (byHead.get(head) ?? byHead.set(head, []).get(head)!).push(it);
    }
    const order = [CASES_GROUP, ...categoryLabels, UNCATEGORIZED_GROUP];
    const sections: AgendaSection[] = [];
    for (const head of order) {
      const items = byHead.get(head);
      if (items && items.length > 0) sections.push({ key: head, head, headClass: GROUP_HEAD, items });
      byHead.delete(head);
    }
    // Off-vocabulary labels (a since-edited config) still render, after the named groups.
    for (const [head, items] of byHead) sections.push({ key: head, head, headClass: GROUP_HEAD, items });
    return sections;
  }
  // Due date (default): the Overdue / Today / This week / Upcoming buckets.
  const groups = new Map<Bucket, WorkItemRow[]>();
  for (const it of open) (groups.get(bucketOf(it)) ?? groups.set(bucketOf(it), []).get(bucketOf(it))!).push(it);
  return ORDER.filter((b) => (groups.get(b)?.length ?? 0) > 0).map((b) => ({
    key: b,
    head: b,
    headClass: BUCKET_HEAD[b],
    items: groups.get(b) ?? [],
  }));
}

// The Repeats picker + its cadence/anchor option lists live in RecurrencePicker
// (shared with the drawer "Edit repeat"); the Due field is the house DateInput.

/** The colour + glyph legend (mine / shared / recurring / overdue). */
function Legend(): ReactNode {
  return (
    <div
      data-testid="hub-mytasks-legend"
      className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-ink-soft"
    >
      <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="w-2.5 h-2.5 rounded-full bg-sage-400" /> Yours</span>
      <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="w-2.5 h-2.5 rounded-full border-2 border-warm-400" /> Shared with you</span>
      <span className="inline-flex items-center gap-1.5"><Repeat size={12} aria-hidden="true" /> Recurring</span>
      <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="w-2.5 h-2.5 rounded-full bg-pepper" /> Overdue</span>
    </div>
  );
}

/** `embedded` mounts the agenda inside the Workspace "My Tasks" tab — the tab
 *  chrome already titles it, so the eyebrow + sand panel are dropped (it renders
 *  bare like its sibling views). Default (false) is the standalone framed card.
 *  `addTaskSignal` lets an outside affordance (the Workspace "+ Task" header
 *  button) open the same inline add-task form: any increment opens it. */
export interface HubMyTasksProps {
  embedded?: boolean;
  addTaskSignal?: number;
  /** LP-2: the viewer's own clients (Your day's read) for the add form's
   *  client picker: first in the results, and the whole source for a
   *  viewer who cannot open the roster. */
  caseload?: ReadonlyArray<ClientLookupPick>;
  /** Where a row opens its drawer. Your day (the default) opens it in
   *  place; the office's Workspace passes its own My Tasks tab. */
  itemHref?: (id: string) => string;
  /** LP-11: the narrow rail form on My Clients. One Filter control that
   *  shows its applied value (no sort, no legend); "shared" and the
   *  repeat said in words, not glyphs; Overdue and Today open, everything
   *  later collapsed under Later. */
  rail?: boolean;
  /** LP-24: the rail's own heading (My Clients' "My Tasks"). The rail
   *  renders it on one line with the office's Filter at the right, so the
   *  first group starts straight under the heading. */
  heading?: ReactNode;
}

export function HubMyTasks({ embedded = false, addTaskSignal = 0, caseload, itemHref, rail = false, heading }: HubMyTasksProps = {}): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const userId = whoami?.user_id ?? '';
  const hrefFor = itemHref ?? ((id: string) => `/t/${tenantSlug}/clients?item=${id}`);
  const canLookupClients = canViewAnyClientRoster(whoami);
  // LP-2: who may act here. The add form, the Repeats picker and the
  // complete tick belong to anyone who can save a task (the office, or an
  // own-tasks holder, whose recurring tasks are their own). A seat that
  // only reads (the board seat) gets the list alone. The server decides on
  // the row either way.
  const canAct = canManageWorkItems(whoami) || canManageOwnTasks(whoami);
  // LP-11 (the owner's look): a care manager's or SPICE seat's list has no
  // filters or categories; the office keeps both.
  const showCategories = canManageWorkItems(whoami);
  // WS-7: due-date order is the default ("task list in date order + overdue");
  // the sort + category filter ride the SERVER read (the pagination lives there).
  const [sort, setSort] = useState<WorkItemSort>('due_date');
  const [categoryFilter, setCategoryFilter] = useState('');
  const agendaQ = useWorkItems(
    tenantSlug,
    { for_user: userId, limit: 100, sort, ...(categoryFilter ? { category: categoryFilter } : {}) },
    { enabled: userId !== '' },
  );
  const completedQ = useCompletedTasks(tenantSlug, { enabled: userId !== '' });
  // The tenant's category vocabulary (config seam; platform default fallback).
  const { data: tenantConfig } = useTenantConfig(tenantSlug, { enabled: tenantSlug !== '' });
  const categories = readTaskCategories(tenantConfig?.config);
  const patchItem = usePatchWorkItem(tenantSlug);
  const completeRecurring = useCompleteRecurringTask(tenantSlug);
  const createTask = useCreateWorkItem(tenantSlug);
  const createSeries = useCreateRecurringSeries(tenantSlug);
  // LP-16: Leave removes the viewer's own collaborator row, never the
  // owner's or another collaborator's. A refusal shows under that row.
  const leaveShared = useRemoveCollaborator(tenantSlug);
  const supportContact = useSupportContact({ enabled: userId !== '' });
  const leave = (t: WorkItemRow): void => {
    if (!leaveShared.isPending) leaveShared.mutate({ id: t.id, userId });
  };
  const leaveButton = (t: WorkItemRow): ReactNode => (
    <Button
      variant="ghost"
      size="sm"
      data-testid={`hub-mytasks-leave-${t.id}`}
      aria-label={`Leave ${rowTitle(t)}`}
      loading={leaveShared.isPending && leaveShared.variables?.id === t.id}
      disabled={leaveShared.isPending}
      onClick={() => leave(t)}
      className="flex-shrink-0"
    >
      Leave
    </Button>
  );
  const leaveError = (t: WorkItemRow, className = 'mt-1'): ReactNode =>
    leaveShared.isError && leaveShared.variables?.id === t.id ? (
      <p role="alert" data-testid={`hub-mytasks-leave-error-${t.id}`} className={`${className} text-xs text-pepper`}>
        {pageWriteText(leaveShared.error, supportContact)}
      </p>
    ) : null;
  const [adding, setAdding] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [dueDate, setDueDate] = useState(''); // one-off task's due date (when Repeats = Doesn't repeat)
  const [newCategory, setNewCategory] = useState(''); // '' = no category
  // LP-2: the client the new task is about (optional).
  const [newClient, setNewClient] = useState<ClientLookupPick | null>(null);
  // Repeats picker state ('' cadence = a one-off task) — one draft, the shared picker.
  const [recur, setRecur] = useState<RecurrenceDraft>(EMPTY_RECURRENCE);
  // Completed section (collapsed by default; the count still shows).
  const [showCompleted, setShowCompleted] = useState(false);
  // The rail's Later group (closed by default; the count still shows).
  const [showLater, setShowLater] = useState(false);
  // LP-24: a rail group shows its first ROW_CAP rows until "Show all".
  const [uncapped, setUncapped] = useState<ReadonlySet<string>>(() => new Set());
  const toggleCap = (key: string): void =>
    setUncapped((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const capped = <T,>(key: string, rows: readonly T[]): readonly T[] =>
    rail && !uncapped.has(key) ? rows.slice(0, ROW_CAP) : rows;

  // The Workspace header "+ Task" bumps the signal; each bump opens the form.
  useEffect(() => {
    if (addTaskSignal > 0) setAdding(true);
  }, [addTaskSignal]);

  // Open items only — a for_user list keeps done tasks, so drop them here.
  const open = agendaQ.rows.filter((r) => r.status !== 'done');
  const sections = sectionsFor(
    sort,
    open,
    categories.map((c) => c.label),
    (t) => taskCategoryLabel(categories, t.category),
  );
  const isLoading = userId === '' || agendaQ.isLoading;
  const filtering = categoryFilter !== '';

  const ready = recurrenceAnchorReady(recur);
  const submitting = createTask.isPending || createSeries.isPending;

  function closeForm(): void {
    setAdding(false);
    setNewTask('');
    setDueDate('');
    setNewCategory('');
    setNewClient(null);
    setRecur(EMPTY_RECURRENCE);
  }

  function addTask(e: React.FormEvent): void {
    e.preventDefault();
    const title = newTask.trim();
    if (!title || submitting || !ready) return;

    // No cadence → a one-off owned task. A cadence → a recurring-series rule
    // (which creates its own first live instance on the agenda).
    if (recur.cadence === '') {
      createTask.mutate(
        { kind: 'task', title, assignee_user_id: userId, due_date: dueDate || null, category: newCategory || null, client_id: newClient?.id ?? null },
        { onSuccess: closeForm },
      );
      return;
    }
    const input: CreateRecurringSeriesInput = {
      title,
      cadence: recur.cadence,
      owner_user_id: userId,
      category: newCategory || null,
      client_id: newClient?.id ?? null,
      ...recurrenceAnchors(recur),
    };
    createSeries.mutate(input, { onSuccess: closeForm });
  }

  // Ticking a recurring task ADVANCES it (logs + rolls the same card forward);
  // a one-off task is marked done.
  function tickTask(t: WorkItemRow): void {
    if (t.recurrence_id) {
      if (!completeRecurring.isPending) completeRecurring.mutate(t.id);
    } else if (!patchItem.isPending) {
      patchItem.mutate({ id: t.id, edits: { status: 'done' } });
    }
  }

  // Restore = the inverse of the done-flip (one-off tasks only; a recurring
  // occurrence is a log row, and restoring one would disturb the series).
  function restoreTask(c: CompletedTaskRow): void {
    if (!c.restorable || patchItem.isPending) return;
    patchItem.mutate({ id: c.work_item_id, edits: { status: 'todo' } });
  }

  /** "Done Jul 3" for a one-off; "Done for Jul 1" for a recurring occurrence. */
  function completedProse(c: CompletedTaskRow): string {
    if (c.is_recurring && c.completed_for) return `Done for ${shortDate(c.completed_for)}`;
    return `Done ${shortDate(c.completed_at)}`;
  }

  const categoryControl = (
    <label className="inline-flex items-center gap-1.5 text-[12px] text-ink-soft">
      {rail ? 'Filter' : 'Category'}
      <SelectField size="compact"
        value={categoryFilter}
        onChange={(e) => setCategoryFilter(e.target.value)}
        aria-label="Filter by category"
        data-testid="hub-mytasks-category-filter"
      >
        <option value="">{rail ? 'All' : 'All categories'}</option>
        {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </SelectField>
    </label>
  );
  // LP-24: the rail's Filter rides the heading line, shown exactly when the
  // list itself is (not while loading, not on an error, not when there is
  // nothing to filter).
  const railFilter =
    rail && showCategories && !isLoading && !agendaQ.isError && (open.length > 0 || filtering);

  // LP-24: the rail's list is a card while it holds anything; with nothing
  // open, nothing completed and no add form, it is the heading and one
  // quiet line (emptiness is quiet).
  const railEmpty =
    rail && !isLoading && !agendaQ.isError && open.length === 0 && !filtering && completedQ.rows.length === 0 && !adding;
  const rootClass = rail ? (railEmpty ? 'border border-transparent p-4' : SECTION_CARD) : embedded ? undefined : 'mb-9';

  return (
    <section data-testid="hub-mytasks" className={rootClass}>
      <div className={embedded ? undefined : 'rounded-[18px] bg-sand p-5 md:p-6'}>
        {!embedded && (
          <div className="mb-4 inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.16em] text-warm-400">
            <span aria-hidden="true" className="h-px w-[22px] bg-warm-400" />
            My tasks
          </div>
        )}

        {rail && (heading !== undefined || railFilter) && (
          // h-7 is the heading's own height; the compact select centres on it.
          <div className="mb-2 flex h-7 items-center justify-between gap-3" data-testid="hub-mytasks-heading-row">
            {heading}
            {railFilter && <div className="ml-auto flex-shrink-0">{categoryControl}</div>}
          </div>
        )}
        {/* Add a task sits first, above every group, in both shapes. */}
        {!isLoading && canAct &&
          (adding ? (
            <form className="mb-4 space-y-2" onSubmit={addTask}>
              <input
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="What needs doing?"
                aria-label="Add a task"
                data-testid="hub-add-task-input"
                autoFocus
                className="w-full rounded-base border border-sage-200 bg-paper px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              />

              {/* Due (and Category for the office) on one line, Repeats on its
                  own line under it, so nothing wraps beside Repeats. */}
              {(recur.cadence === '' || showCategories) && (
              <div className="flex flex-wrap items-center gap-2" data-testid="hub-add-task-due-row">
                {/* One-off path: a real Due date. When a cadence is chosen the
                    cadence's anchor drives the schedule, so this hides. */}
                {recur.cadence === '' && (
                  <label className="flex items-center gap-1.5 text-[13px] text-ink-soft">
                    <span className="w-16 shrink-0">Due</span>
                    <DateInput
                      data-testid="hub-add-task-due"
                      aria-label="Due date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      size="compact"
                    />
                  </label>
                )}
                {/* WS-7: the category at capture (one-off AND recurring — a
                    recurring rule stamps its live instance). '' = none. The
                    office only. */}
                {showCategories && (
                <label className="flex items-center gap-1.5 text-[13px] text-ink-soft">
                  <span className="w-16 shrink-0">Category</span>
                  <SelectField size="compact"
                    data-testid="hub-add-task-category"
                    aria-label="Task category"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                  >
                    <option value="">None</option>
                    {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </SelectField>
                </label>
                )}
              </div>
              )}
              <div className="flex flex-wrap items-center gap-2" data-testid="hub-add-task-repeats-row">
                <RecurrencePicker value={recur} onChange={setRecur} idPrefix="hub" labelClassName="w-16 shrink-0" />
              </div>
              {/* LP-2: the client the task is about (optional). */}
              {newClient ? (
                <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-soft">
                  Client
                  <Chip variant="neutral" data-testid="hub-add-task-client-picked">{newClient.display_name}</Chip>
                  <Button variant="ghost" size="sm" data-testid="hub-add-task-client-clear" onClick={() => setNewClient(null)}>
                    Remove
                  </Button>
                </div>
              ) : (
                <ClientLookupField
                  tenantSlug={tenantSlug}
                  canLookup={canLookupClients}
                  caseload={caseload}
                  onPick={setNewClient}
                  inputId="hub-add-task-client"
                  testIdPrefix="hub-add-task-client"
                  label="Client (optional)"
                  className="relative"
                />
              )}
              {createSeries.isError && (
                <p className="text-xs text-pepper">The repeat didn't save. Check that the end date is after the start date, then save again.</p>
              )}
              {/* Add and Cancel close the form, after every field. */}
              <div className="flex items-center gap-2 pt-1" data-testid="hub-add-task-actions">
                <button
                  type="submit"
                  disabled={submitting || newTask.trim() === '' || !ready}
                  data-testid="hub-add-task-submit"
                  className="rounded-base bg-sage-800 px-3.5 py-2 text-sm font-semibold text-cream disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  data-testid="hub-add-task-cancel"
                  className="rounded-base px-2.5 py-2 text-sm font-medium text-ink-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : rail ? (
            // The rail's one action: a filled house Button, first under the
            // heading, so it reads apart from the group heads below it.
            <Button variant="primary" size="sm" icon={<Plus size={15} aria-hidden="true" />} className="mb-3" data-testid="hub-add-task-reveal" onClick={() => setAdding(true)}>
              Add a task
            </Button>
          ) : (
            <button
              type="button"
              data-testid="hub-add-task-reveal"
              onClick={() => setAdding(true)}
              className="mb-4 flex w-full items-center gap-2 rounded-base border border-dashed border-warm-400/60 px-4 py-3 text-[13px] font-medium text-warm-600 hover:bg-paper/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              <Plus size={15} aria-hidden="true" />
              Add a task
            </button>
          ))}

        {isLoading ? (
          <div className="space-y-2.5"><SkeletonRow /><SkeletonRow /></div>
        ) : agendaQ.isError ? (
          <ErrorBanner error={agendaQ.error} onRetry={agendaQ.refetch} what="your tasks" />
        ) : open.length === 0 && !filtering ? (
          <p className="text-sm text-ink-soft px-1 py-3" data-testid="hub-mytasks-empty">You’re all caught up.</p>
        ) : (
          <div>
            {/* WS-7: sort + category filter ("categories for task list!"). */}
            {!rail && (
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              {!rail && (
              <label className="inline-flex items-center gap-1.5 text-[12px] text-ink-soft">
                Sort
                <SelectField size="compact"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as WorkItemSort)}
                  aria-label="Sort tasks"
                  data-testid="hub-mytasks-sort"
                >
                  {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </SelectField>
              </label>
              )}
              {showCategories && categoryControl}
            </div>
            )}
            {open.length === 0 ? (
              <p className="text-sm text-ink-soft px-1 py-3" data-testid="hub-mytasks-empty">Nothing in this category.</p>
            ) : (
            <>
            {!rail && <Legend />}
            {(() => {
            const renderSection = (section: AgendaSection, showHead = true): ReactNode => {
              const items = section.items;
              const slug = section.key.replace(/\s+/g, '-').toLowerCase();
              return (
                <div key={section.key} className={rail ? 'mb-4 flex flex-col gap-1' : 'mb-5'} data-testid={`hub-mytasks-bucket-${slug}`}>
                  {showHead && section.head !== '' && rail && (
                    <GroupHead
                      label={section.head}
                      count={items.length}
                      tone={BUCKET_TONE[section.key as Bucket] ?? 'neutral'}
                      testId={`hub-mytasks-head-${slug}`}
                    />
                  )}
                  {showHead && section.head !== '' && !rail && (
                    <div className={'text-[11px] uppercase tracking-[0.12em] font-semibold mb-2 mt-1 ' + section.headClass}>
                      {section.head}
                      <span className="opacity-60"> · {items.length}</span>
                    </div>
                  )}
                  <div className={rail ? ROW_LIST : undefined}>
                  {capped(section.key, items).map((t) => {
                    const shared = t.my_role === 'collaborator';
                    const sub = rowSubline(t);
                    const recurring = t.recurrence_id !== null;
                    const isTask = t.kind === 'task';
                    // The date-chip register keys off the ROW's own bucket (not the
                    // section), so overdue stays terracotta under every sort.
                    const rowBucket = bucketOf(t);
                    const categoryChip = isTask ? taskCategoryLabel(categories, t.category) : null;
                    if (rail) {
                      // The rail's one row shape, the Home glance's: the title
                      // at the left, then the client bubble and the date at
                      // the right edge, the date last. The title gives way
                      // first (down to its own width or 8rem, FlooredTitle);
                      // the bubble is capped and truncates past its cap; the
                      // date never does. When the title's floor and the trail
                      // cannot share the line, the trail drops to a second
                      // line, still at the right edge, and the title keeps
                      // its line. A row with no client keeps its date flush
                      // right. "Shared with you" and a repeat's words sit on
                      // a quiet line under. The checkbox sits on the title's
                      // first line.
                      // LP-24: under the Today head a row does not say
                      // "Today" again; every other row keeps its date.
                      const due = t.due_date && rowBucket !== 'Today' ? shortDate(t.due_date) : null;
                      const client = isTask && t.client_id && t.client_display_name ? { id: t.client_id, name: t.client_display_name } : null;
                      // A repeating task says its repeat in words, last.
                      const repeat = recurring && t.recurrence_summary ? t.recurrence_summary : null;
                      return (
                        <div
                          key={t.id}
                          data-testid="hub-mytasks-row"
                          data-role={shared ? 'collaborator' : 'owner'}
                          className="flex items-start gap-3 py-2.5"
                        >
                          <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center" data-testid={`hub-mytasks-slot-${t.id}`}>
                            {isTask && canAct ? (
                              <input
                                type="checkbox"
                                checked={false}
                                data-testid={`hub-mytasks-complete-${t.id}`}
                                disabled={recurring ? completeRecurring.isPending : patchItem.isPending}
                                aria-label={recurring ? `Complete ${rowTitle(t)} (repeats)` : `Mark ${rowTitle(t)} done`}
                                onChange={() => tickTask(t)}
                                className="h-[18px] w-[18px] cursor-pointer appearance-none rounded-full border-2 border-sage-400 hover:bg-sage-50 disabled:opacity-50"
                              />
                            ) : shared ? (
                              <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full border-2 border-warm-400" />
                            ) : (
                              <span aria-label="Yours" title="Yours" className="h-2.5 w-2.5 rounded-full bg-sage-400" />
                            )}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`hub-mytasks-line-${t.id}`}>
                              <Link
                                to={hrefFor(t.id)}
                                data-testid={`hub-mytasks-title-${t.id}`}
                                title={rowTitle(t)}
                                className="flex flex-1 items-center rounded-base text-sm font-semibold leading-5 text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                              >
                                <FlooredTitle text={rowTitle(t)} className="" />
                              </Link>
                              {(client !== null || due !== null) && (
                                <span className="ml-auto flex max-w-full flex-shrink-0 items-center gap-2 text-xs" data-testid={`hub-mytasks-trail-${t.id}`}>
                                  {client !== null && (
                                    <Link
                                      to={`/t/${tenantSlug}/clients/${client.id}`}
                                      data-testid={`hub-mytasks-client-${t.id}`}
                                      title={client.name}
                                      className={`min-w-0 max-w-[12rem] truncate ${CLIENT_BUBBLE_CLASS}`}
                                    >
                                      {client.name}
                                    </Link>
                                  )}
                                  {due !== null && (
                                    <span data-testid={`hub-mytasks-due-${t.id}`} className="flex-shrink-0 whitespace-nowrap text-ink-soft">{due}</span>
                                  )}
                                </span>
                              )}
                            </div>
                            {(shared || repeat !== null) && (
                              <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-ink-soft" data-testid={`hub-mytasks-meta-${t.id}`}>
                                {shared && <span data-testid={`hub-mytasks-shared-${t.id}`}>Shared with you</span>}
                                {shared && repeat !== null && <span aria-hidden="true">·</span>}
                                {repeat !== null && <span>{repeat}</span>}
                              </div>
                            )}
                            {shared && leaveError(t)}
                          </div>
                          {shared && canAct && leaveButton(t)}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={t.id}
                        data-testid="hub-mytasks-row"
                        data-role={shared ? 'collaborator' : 'owner'}
                        className={
                          rail
                            ? 'flex items-center gap-3 border-b border-sage-100 py-2.5'
                            : 'flex flex-wrap items-center gap-3 bg-paper rounded-base border border-sand px-4 py-3 mt-2.5 border-l-4 ' +
                              (shared ? 'border-l-warm-400' : 'border-l-sage-400')
                        }
                      >
                        {/* The slot: a task = a tickable circle; an owned case = a sage
                            dot; a shared case = a gold ring. */}
                        <span className="flex w-5 flex-shrink-0 items-center justify-center">
                          {isTask && canAct ? (
                            <input
                              type="checkbox"
                              checked={false}
                              data-testid={`hub-mytasks-complete-${t.id}`}
                              disabled={recurring ? completeRecurring.isPending : patchItem.isPending}
                              aria-label={recurring ? `Complete ${rowTitle(t)} (repeats)` : `Mark ${rowTitle(t)} done`}
                              onChange={() => tickTask(t)}
                              className="h-[18px] w-[18px] cursor-pointer appearance-none rounded-full border-2 border-sage-400 hover:bg-sage-50 disabled:opacity-50"
                            />
                          ) : shared ? (
                            <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full border-2 border-warm-400" />
                          ) : (
                            <span aria-label="Yours" title="Yours" className="h-2.5 w-2.5 rounded-full bg-sage-400" />
                          )}
                        </span>

                        {/* LP-43: the title and the client share one head that
                            wraps inside itself, so on a narrow row the name
                            drops under the title and the date keeps the
                            title's line. The title gives way first, down to
                            its own width or 8rem (FlooredTitle; the head never
                            narrows below that), then the name; the subline
                            sets no width of its own (max-w-0, min-w-full). */}
                        <div data-testid={`hub-mytasks-lead-${t.id}`} className="flex min-w-[8rem] flex-1 flex-wrap items-center gap-x-3 gap-y-0.5">
                          <Link
                            to={hrefFor(t.id)}
                            className="flex-1 rounded-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                          >
                            <div className="flex items-center gap-1.5">
                              {recurring && !rail && (
                                <Repeat size={13} aria-hidden="true" data-testid="hub-mytasks-recurring" className="flex-shrink-0 text-sage-500" />
                              )}
                              <FlooredTitle
                                text={rowTitle(t)}
                                className={isTask ? 'text-[14.5px] font-semibold text-ink' : 'font-display text-[15.5px] font-semibold text-sage-800'}
                              />
                            </div>
                            {sub && <div className="mt-0.5 min-w-full max-w-0 truncate text-[12.5px] text-ink-soft">{sub}</div>}
                          </Link>
                          {/* LP-2: the client the task is about, a link to the record.
                              LP-43: the name in the client bubble, a link, in the
                              date pills' family a step deeper. */}
                          {isTask && t.client_id && t.client_display_name && (
                            <Link
                              to={`/t/${tenantSlug}/clients/${t.client_id}`}
                              data-testid={`hub-mytasks-client-${t.id}`}
                              title={t.client_display_name}
                              className={`min-w-0 max-w-full truncate text-sm ${CLIENT_BUBBLE_CLASS}`}
                            >
                              {t.client_display_name}
                            </Link>
                          )}
                        </div>

                        {/* LP-16: a card shared with you says so in words.
                            LP-43: Leave sits beside those words, ahead of the
                            date, so the date is last on every row. */}
                        {shared && (
                          <span data-testid={`hub-mytasks-shared-${t.id}`} className="flex-shrink-0 text-xs text-ink-soft">Shared with you</span>
                        )}
                        {shared && canAct && leaveButton(t)}
                        {/* Shared: the OWNER's champagne avatar (whose case this is). */}
                        {shared && !rail && (
                          <span
                            data-testid="hub-mytasks-owner-avatar"
                            aria-label={t.assignee_name ? `Owned by ${t.assignee_name}` : 'No owner yet'}
                            title={t.assignee_name ? `Owned by ${t.assignee_name}` : 'No owner yet'}
                            className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gold-soft text-[11px] font-semibold text-warm-700"
                          >
                            {initials(t.assignee_name)}
                          </span>
                        )}

                        {categoryChip && (
                          <span
                            data-testid="hub-mytasks-category-chip"
                            className={`flex-shrink-0 rounded-pill px-2 py-0.5 text-[10.5px] font-semibold ${taskCategoryChipClass(t.category)}`}
                          >
                            {categoryChip}
                          </span>
                        )}

                        {t.due_date && (
                          <span className={'ml-auto flex-shrink-0 rounded-pill px-2.5 py-1 text-[11.5px] font-semibold ' + BUCKET_CHIP[rowBucket]}>
                            {rowBucket === 'Today' ? 'Today' : shortDate(t.due_date)}
                          </span>
                        )}
                        {shared && leaveError(t, 'basis-full')}
                      </div>
                    );
                  })}
                  </div>
                  {rail && (
                    <ShowAllToggle
                      total={items.length}
                      open={uncapped.has(section.key)}
                      onToggle={() => toggleCap(section.key)}
                      testId={`hub-mytasks-show-all-${slug}`}
                    />
                  )}
                </div>
              );
            };
            // The rail keeps Overdue, Today and This week open, in that
            // order; only Upcoming (beyond seven days, or undated) folds,
            // under "Later". The other sorts group by their own heads and
            // stay open.
            const byDue = sort === 'due_date';
            const now = rail && byDue ? sections.filter((x) => x.key !== 'Upcoming') : sections;
            const later = rail && byDue ? sections.filter((x) => x.key === 'Upcoming') : [];
            const laterCount = later.reduce((n, x) => n + x.items.length, 0);
            return (
              <>
                {now.map((x) => renderSection(x))}
                {later.length > 0 && (
                  <div data-testid="hub-mytasks-later" className="mb-4">
                    <GroupHead
                      label="Later"
                      count={laterCount}
                      tone="neutral"
                      expanded={showLater}
                      onToggle={() => setShowLater((s) => !s)}
                      controls="hub-mytasks-later-rows"
                      testId="hub-mytasks-later-toggle"
                    />
                    {/* Mounted while closed, as the old <details> was. */}
                    <div id="hub-mytasks-later-rows" data-testid="hub-mytasks-later-rows" hidden={!showLater}>
                      {later.map((x) => renderSection(x, false))}
                    </div>
                  </div>
                )}
              </>
            );
            })()}
            </>
            )}
          </div>
        )}

        {/* Completed section — collapsed by default at the bottom. A finished
            task never vanishes: the last 30 days live here, newest first.
            One-offs restore (the status flips back); a recurring occurrence is
            the advance-in-place log row, shown read-only so the live series is
            never disturbed. Hidden entirely when there is nothing to show. */}
        {!isLoading && completedQ.rows.length > 0 && (
          <div className={rail ? 'mt-4' : 'mt-5'} data-testid="hub-mytasks-completed">
            {rail ? (
              <GroupHead
                label="Completed"
                count={completedQ.rows.length}
                tone="neutral"
                expanded={showCompleted}
                onToggle={() => setShowCompleted((s) => !s)}
                testId="hub-mytasks-completed-toggle"
              />
            ) : (
            <button
              type="button"
              data-testid="hub-mytasks-completed-toggle"
              aria-expanded={showCompleted}
              onClick={() => setShowCompleted((s) => !s)}
              className="flex items-center gap-1.5 rounded-base px-1 py-1 text-[11.5px] font-semibold uppercase tracking-[0.12em] text-sage-400 hover:text-sage-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              <ChevronRight
                size={13}
                aria-hidden="true"
                className={'transition-transform ' + (showCompleted ? 'rotate-90' : '')}
              />
              Completed
              <span className="opacity-60"> · {completedQ.rows.length}</span>
            </button>
            )}

            {showCompleted && (
              <div className={rail ? `mt-1 ${ROW_LIST}` : 'mt-1.5'}>
                {capped('completed', completedQ.rows).map((c) => (
                  <div
                    key={c.id}
                    data-testid="hub-mytasks-completed-row"
                    className={
                      rail
                        ? 'flex items-center gap-3 py-2.5'
                        : 'mt-2 flex items-center gap-3 rounded-base border border-sand bg-paper/60 px-4 py-2.5'
                    }
                  >
                    <span className="flex w-5 flex-shrink-0 items-center justify-center">
                      <span
                        aria-hidden="true"
                        className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-sage-400 text-cream"
                      >
                        <Check size={12} />
                      </span>
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {c.is_recurring && (
                          <Repeat size={13} aria-hidden="true" className="flex-shrink-0 text-sage-500" />
                        )}
                        <span className="truncate text-[14px] font-medium text-ink-soft">{c.title}</span>
                      </div>
                      <div className="mt-0.5 truncate text-[12px] text-sage-400">{completedProse(c)}</div>
                    </div>
                    {c.restorable && (
                      <button
                        type="button"
                        data-testid="hub-mytasks-restore"
                        disabled={patchItem.isPending}
                        onClick={() => restoreTask(c)}
                        className="flex-shrink-0 rounded-base px-2 py-1 text-xs font-semibold text-sage-800 underline hover:text-sage-600 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ))}
                {rail && (
                  <ShowAllToggle
                    total={completedQ.rows.length}
                    open={uncapped.has('completed')}
                    onToggle={() => toggleCap('completed')}
                    testId="hub-mytasks-show-all-completed"
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
